import { get } from "lodash-es";
import { type FC } from "react";
import { useSelector } from "react-redux";
import { ColoredLabel } from "widgets";

import { getCssColor } from "../../../../app-utils/color.js";
import { type OrganismProperty } from "../../../../graphql.js";
import { type ITableColumn } from "../../../../redux/modules/table-data.js";
import { type IAppState } from "../../../../redux/reducer.js";
import { CELL_TYPE } from "../../../../settings/settings.js";
import { type IAgGridCellProps } from "../cell-base-interfaces.js";
import ListCellContent, { type ListCellItem } from "./core/ListCellContent.js";
import { TableCell } from "./core/TableCell.js";

export const getPhenotypeDisplayValue = (value: string, data, colDef, editMode) => {
  let color = null;
  // we have to find out the phenotype data to extract the color
  if (colDef.type === CELL_TYPE.STRING || colDef.type === CELL_TYPE.STRING_LIST) {
    let orgProperty: OrganismProperty = get(data, colDef.groupField);

    // in edit mode we rely on the metadata stored in redux, for resolving the color from the value
    if (editMode && colDef.editable({ data, colDef })) {
      const phenotypes = useSelector<IAppState, OrganismProperty[]>((state) => {
        const column: ITableColumn = state.dataEditing.columns.find((column) => column.field === colDef.field);
        return state.dataEditing.orgPropertyValuesLookup[column.columnId];
      });
      orgProperty = phenotypes?.find((phenotype) => phenotype.value === value);
    }
    color = orgProperty?.color ? getCssColor(orgProperty?.color) : null;
    return (
      <ColoredLabel color={color} transparent>
        {value}
      </ColoredLabel>
    );
  }

  return value;
};

const PhenotypeCell: FC<IAgGridCellProps> = (props) => {
  const { data, colDef, context, value } = props;

  if (value == null || !data) {
    return null;
  }

  const multiValues = Array.isArray(value) ? value : [value];

  const items: ListCellItem[] = multiValues.map((phenotype: string) => ({
    value: getPhenotypeDisplayValue(phenotype, data, colDef, context?.editMode),
    tooltip: phenotype,
  }));

  return (
    <TableCell
      style={{
        textAlign:
          colDef.type === CELL_TYPE.NUMBER || colDef.type === CELL_TYPE.INTEGER || colDef.type === CELL_TYPE.NUMBER_LIST
            ? "right"
            : undefined,
      }}
    >
      <ListCellContent items={items} colDef={props.colDef} context={props.context} />
    </TableCell>
  );
};

export default PhenotypeCell;
